import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        // keep trying
      }
      tries += 1;
      if (tries >= MAX_TRIES) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-40 text-center max-w-xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-primary animate-spin mb-8" />
            <h1 className="font-display text-5xl mb-4">Confirming your order…</h1>
            <p className="font-body text-muted-foreground">Hang tight while we verify your payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto border border-primary flex items-center justify-center mb-8"><Check className="w-8 h-8 text-primary" /></div>
            <h1 className="font-display text-6xl mb-4">Order confirmed</h1>
            <p className="font-body text-muted-foreground mb-2">Your custom piece is entering production.</p>
            {amount != null && <p className="font-num text-xl text-primary mb-8">{money(amount)} paid</p>}
            <Link to="/shop" className="btn-forge inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white">
              Continue shopping <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-primary mb-8" />
            <h1 className="font-display text-5xl mb-4">Payment processing</h1>
            <p className="font-body text-muted-foreground mb-8">This is taking a moment. We'll email your confirmation shortly.</p>
            <Link to="/" className="link-underline font-num uppercase tracking-widest text-sm text-primary">Back to home</Link>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="w-14 h-14 mx-auto text-primary mb-8" />
            <h1 className="font-display text-5xl mb-4">Something went wrong</h1>
            <p className="font-body text-muted-foreground mb-8">We couldn't confirm this payment. If you were charged, contact us and we'll sort it out.</p>
            <Link to="/cart" className="btn-forge inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white">
              Return to cart <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
